import { router, useFocusEffect, useLocalSearchParams, useNavigation } from 'expo-router';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { Divider } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetPaymentMethodList } from '@/api/hooks/paymentAccountsHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import DotsLoader from '@/components/DotsLoader';
import TokneizedACHView from '@/components/paymentMethods/TokenizedACHView';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';

export interface TokenizedACHData {
  account_number_last_four_digits: string;
  account_type: string;
  check_key?: string;
  check_type: string;
  name_on_check: string;
}

const AchList = () => {
  const navigation = useNavigation();
  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const [payloadState, setPayloadState] = useState<{
    donationData: string;
    individualNuveiUser: string;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
  }>();

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        donationData: string;
        individualNuveiUser: string;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.donationData)
      navigation.setOptions({
        headerTitle: () => <Title label={i18n.t('donation.select_payment_method')} />
      });
  }, [payloadState?.donationData, navigation]);

  const { data, refetch, isPending } = useGetPaymentMethodList('');

  useFocusEffect(
    useCallback(() => {
      refetch();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );

  const achAccounts = data?.paymentsafe_checks.flatMap((achAccount) => achAccount);

  return (
    <SafeAreaView style={styles.flexOne} edges={['bottom']}>
      <View style={styles.container}>
        {isPending ? (
          <View style={styles.loader}>
            <DotsLoader />
          </View>
        ) : (
          <FlatList
            data={achAccounts}
            style={styles.list}
            ItemSeparatorComponent={() => <Divider style={styles.separator} />}
            ListEmptyComponent={<Text style={styles.emptyList}>{i18n.t('payment_methods.ach_empty_list')}</Text>}
            keyExtractor={(_, index) => index.toString()}
            renderItem={({ item }) => (
              <TokneizedACHView
                tokenizedAch={item}
                onDelete={refetch}
                {...((payloadState?.donationData || payloadState?.routeWithSelectedMethod) && {
                  onPress: async () => {
                    const newPayload = { ...payloadState, tokenizedACH: JSON.stringify(item) };

                    const encrypted = await encryptObject(newPayload);

                    router.navigate({
                      pathname: '/(app)/paymentMethods/billingAddress',
                      params: {
                        payload: encodeURIComponent(encrypted)
                      }
                    });
                  }
                })}
              />
            )}
            ListFooterComponentStyle={styles.footer}
            ListFooterComponent={() => {
              if (payloadState?.donationData || payloadState?.routeWithSelectedMethod)
                return (
                  <BrandedButton
                    style={styles.addACHButton}
                    onPress={async () => {
                      const newPayload = { ...payloadState };

                      const encrypted = await encryptObject(newPayload);

                      router.navigate({
                        pathname: '/(app)/paymentMethods/addAch',
                        params: {
                          payload: encodeURIComponent(encrypted)
                        }
                      });
                    }}>
                    {i18n.t('payment_methods.add_ach')}
                  </BrandedButton>
                );
            }}
          />
        )}
      </View>
    </SafeAreaView>
  );
};

export default AchList;

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  list: {
    flex: 1
  },

  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },

  addACHButton: {
    width: '100%'
  },

  container: {
    flex: 1,
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0
  },

  separator: {
    marginVertical: 10
  },

  footer: {
    flex: 1,
    paddingVertical: 20
  },

  emptyList: {
    alignSelf: 'center',
    marginVertical: 25,
    fontSize: 14
  }
});
